import { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { pageTypeSubstitutes } from '../../constants/substitutes';
import { ILayout } from '../../types/layout';
import useGlobalLoader from '../app/useGlobalLoader';
import useRest from '../useRest';
import { LayoutContext } from '../../contexts/layout';
import { saveMenuData } from '../../redux/actions/menu';
import { clearLayout as clearLayoutAction, setLayout } from '../../redux/actions/layouts';
import { layoutStateSelector } from '../../redux/selectors/layouts';

const useLayout = (layoutId: string, manual = false) => {
    const dispatch = useDispatch();
    const gl = useGlobalLoader();
    const rest = useRest();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const layout = useSelector(layoutStateSelector(layoutId));

    const { updateLayout, setUpdateLayout } = useContext(LayoutContext);

    const location = useLocation();

    const { objectName, pageType, objectId } = params;

    const getLayout = async (layoutLink?: string) => {
        if (!manual) {
            gl.toggleLoader({
                loading: true,
                title: 'Загрузка макета',
            });
        }

        clearLayout();

        const urlSearchParams = new URLSearchParams();
        let layoutUrl = typeof layoutLink === 'string' ? layoutLink : `/${objectName}`;

        const body: Record<string, any> = {};

        if (typeof layoutLink === 'undefined') {
            if (typeof pageType === 'string') {
                layoutUrl += `/${pageTypeSubstitutes[pageType] || pageType}/layout`;
            }

            if (typeof objectId === 'string') {
                urlSearchParams.append('id', objectId);
            }

            if (searchParams.get('type')) {
                urlSearchParams.append('type', searchParams.get('type') as string);
            }

            if (searchParams.get('sortColumn')) {
                body.sort = {
                    column: searchParams.get('sortColumn'),
                    order: searchParams.get('sortOrder') || 'asc',
                };
            }

            if (searchParams.get('page')) {
                body.page = searchParams.get('page');
            }

            if (searchParams.get('sizePerPage')) {
                body.pageSize = searchParams.get('sizePerPage');
            }

            if (searchParams.get('search')) {
                body.search = searchParams.get('search');
            }
        }

        if (Array.from(urlSearchParams).length > 0) {
            layoutUrl += `?${urlSearchParams.toString()}`;
        }

        const result = await rest.fetch({
            method: 'post',
            objectName: layoutUrl,
            data: body,
        });

        if (!result.isError && result.response?.data) {
            const { data } = result.response as { data: ILayout };
            dispatch(setLayout(layoutId, data));
            if (data.menu instanceof Object) {
                dispatch(saveMenuData(data.menu));
            }
        }

        if (!manual) {
            gl.toggleLoader({
                loading: false,
                title: 'Загрузка макета',
            });
        }
    };

    const clearLayout = () => {
        dispatch(clearLayoutAction(layoutId));
    };

    useEffect(() => {
        if (!manual) {
            getLayout();
        }
    }, [location.pathname.toString(), searchParams.get('type')]);

    useEffect(() => {
        if (!manual && updateLayout) {
            getLayout();
        }
    }, [updateLayout]);

    return {
        clearLayout,
        getLayout,
        updateLayout: () => setUpdateLayout(true),
        layout,
    };
};

export default useLayout;
